// Tiny console wrapper that silences step-trace NOISE on production builds while keeping real
// problems (warn/error) visible on EVERY build — including beta.
//
// WHY THE SPLIT (issue #335):
//   - The bridge path emits dozens of step-by-step `console.*` traces per transfer. On the prod
//     bundle these flood the browser console with no value to end users — the noise #335 targets.
//   - `debug` / `info` / `log` are therefore NO-OPS when `import.meta.env.PROD`.
//   - `warn` / `error` ALWAYS fire. Gating them behind `!PROD` would blind BOTH:
//       (a) beta debugging — `beta.parsedefi.com` is itself a PRODUCTION build
//           (`import.meta.env.PROD === true`), yet it is our primary live-debugging surface; and
//       (b) the #303 `SOLANA_SEND_REVERT` capture in `useDexBridge.ts`, which rides on `error` and
//           feeds a user-facing error surface.
//     Real problems must stay visible on beta AND prod.
//
// `import.meta.env.PROD` is Vite's build-time flag (the same gate already used in
// `config/network-init.ts`): `true` for any `vite build` output (app + beta), `false` in `vite dev`
// and under vitest. It is read PER CALL so a `vite build` statically eliminates the guarded branch,
// and tests can flip it with `vi.stubEnv('PROD', …)` without re-importing this module.
//
// Drop-in: every method mirrors its `console` namesake's variadic signature.

/* eslint-disable no-console -- this module is the ONE sanctioned wrapper around console.* (#335) */

export const log = {
  /** Verbose step-trace. Silent on prod builds (app + beta); prints in dev. */
  debug: (...args: unknown[]): void => {
    if (!import.meta.env.PROD) console.debug(...args)
  },
  /** Informational progress. Silent on prod builds (app + beta); prints in dev. */
  info: (...args: unknown[]): void => {
    if (!import.meta.env.PROD) console.info(...args)
  },
  /** Plain trace. Silent on prod builds (app + beta); prints in dev. */
  log: (...args: unknown[]): void => {
    if (!import.meta.env.PROD) console.log(...args)
  },
  /** A real problem worth surfacing. ALWAYS fires — kept on beta AND prod. */
  warn: (...args: unknown[]): void => {
    console.warn(...args)
  },
  /** A real error worth surfacing. ALWAYS fires — kept on beta AND prod (powers the #303 capture). */
  error: (...args: unknown[]): void => {
    console.error(...args)
  },
}

export default log
